import {Component, OnInit, ViewChild} from '@angular/core';
import {TagDtoModel} from '../shared/models/tag.dto.model';
import {LocalizationService} from '../shared/services/localization.service';
import {UntypedFormBuilder, Validators} from '@angular/forms';
import {LoaderService} from '../shared/services/loader.service';
import {ConfirmationService, LazyLoadEvent, MessageService} from 'primeng/api';
import {PaginationArgs, PaginationPage} from '../shared/models/pagination.args';
import {FileUpload} from 'primeng/fileupload';
import {ErrorStateMatcher} from '@angular/material/core';
import {TagService} from '../shared/services/tag.service';
import {ColorPicker} from 'primeng/colorpicker';

@Component({
    selector: 'njoy-tags',
    templateUrl: './tags.component.html',
    styleUrls: ['./tags.component.css']
})
export class TagsComponent implements OnInit {

    public tags: TagDtoModel[] = [];
    public newTag: TagDtoModel;
    public selectedTag: TagDtoModel;

    public addNewTagDialog = false;

    public paginationArgs: PaginationArgs = {pageNumber: 0, pageSize: 10};
    public totalRecords = 0;
    public first = 0;
    public row = 0;

    public modalTitle = '';
    public tagForm;
    @ViewChild('fileUpload')
    public fileUploader: FileUpload;
    public matcher = new ErrorStateMatcher();
    public uploadedFile;
    public noFileUploaded = false;
    public displayImage = false;

    constructor(private tagService: TagService,
                private localizationService: LocalizationService,
                private formBuilder: UntypedFormBuilder,
                private loaderService: LoaderService,
                private messageService: MessageService,
                private confirmationService: ConfirmationService) {
    }

    ngOnInit(): void {
        this.fetchData();

    }

    public addNew(): void {
        this.uploadedFile = undefined;
        this.modalTitle = this.localizationService.translate('TAGS.ADD_NEW_MODAL_TITLE');
        this.addNewTagDialog = true;
        this.displayImage = false;
        this.noFileUploaded = false;
        this.initEmtpyFormValue();
    }

    private initEmtpyFormValue(tag?: TagDtoModel): void {
        this.tagForm = this.formBuilder.group({
            label: [tag ? tag.label : '', [
                Validators.required
            ]],
            textColor: [tag ? tag.textColor : '#000000', [
                Validators.required
            ]],
            backgroundColor: [tag ? tag.backgroundColor : '#000000', [
                Validators.required
            ]]
        });
        this.selectedTag = tag ? {...tag} : undefined;
    }

    get getControl() {
        return this.tagForm.controls;
    }

    public addOrUpdateTag(): void {
        this.displayRequiredMessage();
        if (this.tagForm.status === 'VALID') {
            if (this.modalTitle === this.localizationService.translate('TAGS.ADD_NEW_MODAL_TITLE')) {
                this.addNewTag();
            } else {
                this.updateTag();
            }
        }
    }

    private displayRequiredMessage(): void {
        this.tagForm.touched = true;
        this.noFileUploaded = false;
        const keys = Object.keys(this.tagForm.controls);
        keys.forEach((element: any) => {
            this.tagForm.controls[element].touched = true;
        });
    }

    private fetchData(): void {
        this.addNewTagDialog = false;
        this.loaderService.startLoading();
        this.tagService.findPageTagsDTO(this.paginationArgs).subscribe(
            (data: PaginationPage<TagDtoModel>) => {
                this.tags = data.content;
                this.totalRecords = data.totalElements;
                this.row = this.paginationArgs.pageSize;
                this.loaderService.stopLoading();
            }, ((err: any) => {
                this.loaderService.stopLoading();
                console.log(err);
            })
        );

    }

    public onLazyLoad(event: LazyLoadEvent) {
        this.paginationArgs = {pageNumber: event.first / event.rows, pageSize: event.rows};
        this.fetchData();
    }

    public openUpdateModal(tag: TagDtoModel): void {
        this.modalTitle = this.localizationService.translate('TAGS.UPDATE_MODAL_TITLE');
        this.addNewTagDialog = true;
        this.initEmtpyFormValue(tag);
    }

    public confirmDeleteTag(tag: TagDtoModel): void {
        this.confirmationService.confirm({
            message: this.localizationService.translate('TAGS.DELETE_CONFIRMATION_MESSAGE') + tag.label,
            accept: () => {
                this.deleteTag(tag.uuid);
            }
        });
    }

    private deleteTag(uuid: string): void {
        this.tagService.deleteTag(uuid).subscribe(
            () => {
                this.loaderService.stopLoading();
                this.displaySuccessMessage('CLIENT.SUCCESS_SUMMARY', 'TAGS.DELETE_SUCCESS_DETAIL');
            }, (err: any) => {
                console.log(err);
                this.loaderService.stopLoading();
            }
        );
    }

    private displaySuccessMessage(summary: string, detail: string): void {
        this.messageService.add({
            severity: 'success',
            summary: this.localizationService.translate(summary),
            detail: this.localizationService.translate(detail)
        });
        this.addNewTagDialog = false;
        this.fetchData();
    }

    private addNewTag(): void {
        this.patchValues();
        this.tagService.persistTag(this.newTag).subscribe(() => {
            this.loaderService.stopLoading();
            this.displaySuccessMessage('CLIENT.SUCCESS_SUMMARY', 'TAGS.ADD_SUCCESS_DETAIL');
        }, (err: any) => {
            console.log(err);
            this.loaderService.stopLoading();
        });

    }

    private updateTag(): void {
        this.patchValues(true);
        this.tagService.updateTag(this.selectedTag).subscribe(() => {
            this.loaderService.stopLoading();
            this.displaySuccessMessage('CLIENT.SUCCESS_SUMMARY', 'TAGS.UPDATE_SUCCESS_DETAIL');
        }, (err: any) => {
            console.log(err);
            this.loaderService.stopLoading();
        });
    }

    private patchValues(update?: boolean): void {
        if (update) {
            this.selectedTag.label = this.tagForm.value.label;
            this.selectedTag.textColor = this.tagForm.value.textColor;
            this.selectedTag.backgroundColor = this.tagForm.value.backgroundColor;
        } else {
            this.newTag = new TagDtoModel();
            this.newTag.label = this.tagForm.value.label;
            this.newTag.textColor = this.tagForm.value.textColor;
            this.newTag.backgroundColor = this.tagForm.value.backgroundColor;
        }
    }

    public onColorChanged(color: ColorPicker): void {
        // update form value manually
        color.writeValue(color.inputBgColor);
        color.onModelChange(color.inputBgColor);
    }
}
